import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MockViolation, ScanResult } from '../types/mockdetector.types';
import { 
  MOCK_DATA_PATTERNS, 
  STATISTICAL_CLAIM_PATTERNS,
  STATIC_DATA_PATTERNS
} from '../utils/scanPatterns';
import { scanReactCode } from '../utils/reactPatterns';
import { generateAllAutoFixes } from '../utils/autoFix';
import { calculateHealthScore, ProjectHealthScore } from '../utils/healthScore';
import { detectProjectSize, getAllRoutes } from '../utils/projectSizeDetection';
import { DEFAULT_SCAN_CONFIGS, ProjectSize, SourceFileScanConfig } from '../types/scanConfig.types';
import { useClientSourceScanner } from './useClientSourceScanner';
import { calculateViolationConfidence } from '../utils/confidenceScoring';
import { enhanceViolationWithFix } from '../utils/contextualAutoFix';
// Optional: tableMapping can be configured by developer for their own Supabase
// import { resolveTable } from '../utils/tableMapping';

export const useMockDetection = () => {
  const navigate = useNavigate();
  const [isScanning, setIsScanning] = useState(false);
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);
  const [currentScanProgress, setCurrentScanProgress] = useState<string>('');
  const [isDeepScanning, setIsDeepScanning] = useState(false);
  const [healthScore, setHealthScore] = useState<ProjectHealthScore | null>(null);
  
  // ✅ Initialize client-side scanner at top-level (React hooks rule compliance)
  const { scanProjectFiles, progress: sourceFileProgress, filesScanned, totalFiles } = useClientSourceScanner();

  const scanDOM = (): MockViolation[] => {
    const violations: MockViolation[] = [];
    // DOM scanning for mock data patterns removed - focus on code/UI scanning
    return violations;
  };

  // scanUIComponents() REMOVED - caused 90% false positives
  // Focus on source code patterns instead (higher precision)

  const scanStatisticalClaims = (): MockViolation[] => {
    const violations: MockViolation[] = [];
    
    // Get all text BUT exclude MockDetector's UI and toast notifications
    let allText = document.body.innerText;
    
    // Exclude MockDetector widget
    const mockDetectorElement = document.querySelector('[data-mockdetector-ignore]');
    if (mockDetectorElement) {
      const mockDetectorText = mockDetectorElement.textContent || '';
      allText = allText.replace(mockDetectorText, '');
    }
    
    // Exclude toast notifications
    const toastContainer = document.querySelector('[data-sonner-toaster]');
    if (toastContainer) {
      const toastText = toastContainer.textContent || '';
      allText = allText.replace(toastText, '');
    }
    
    const lines = allText.split('\n');
    const seenClaims = new Set<string>(); // Deduplicate exact same claims

    lines.forEach((line, index) => {
      STATISTICAL_CLAIM_PATTERNS.forEach(({ pattern, severity, description }) => {
        const matches = line.matchAll(pattern);
        for (const match of matches) {
          const claimText = match[0];
          const claimKey = `${description}-${claimText}`; // Unique key per claim type + text
          
          // Skip if we've already found this exact claim
          if (seenClaims.has(claimKey)) return;
          seenClaims.add(claimKey);
          
          violations.push({
            type: 'mock_data',
            severity,
            line: index + 1,
            code: line.trim(),
            description: `${description}: "${claimText}"`,
            fix: 'Verify this claim with real-time data from your database or API'
          });
        }
      });
    });

    return violations;
  };

  // NOTE: Data consistency validation removed - this is a standalone tool
  // Developers can optionally implement their own data validation by extending this hook

  const scanCode = (codeText: string, fileName?: string): MockViolation[] => {
    const violations: MockViolation[] = [];
    const lines = codeText.split('\n');
    
    // ✅ NEW: Smart whitelisting import (add at top of file later)
    // For now, inline whitelist check
    const isWhitelistedFile = (file?: string): boolean => {
      if (!file) return false;
      const whitelistPatterns = [
        /scanPatterns\.ts$/,
        /mockDetector/i,
        /databasePatterns\.ts$/,
        /reactPatterns\.ts$/,
        /smartWhitelist\.ts$/,
        /\.config\.(ts|js)$/,
        /tailwind\.config/,
        /vite\.config/,
      ];
      return whitelistPatterns.some(p => p.test(file));
    };
    
    const isWhitelistedCode = (line: string): boolean => {
      const patterns = [
        /VITE_SUPABASE_PUBLISHABLE_KEY/,
        /VITE_SUPABASE_URL/,
        /\.from\(['"][\w_]+['"]\)/,
        /\.select\(/,
        /\.limit\(\d+\)/,
        /^import\s+/,
        /^export\s+/,
        /CRITICAL.*REAL.*AUTHENTIC/,
      ];
      return patterns.some(p => p.test(line));
    };
    
    // Keywords to skip (MockDetector's own UI text)
    const skipKeywords = [
      'MockDetector',
      'Quick Scan',
      'Scan All Routes',
      'Total Issues',
      'Scanned:',
      'Generated by MockDetector',
      'Mock Data Violations',
      'Security Issues',
      'Scan Duration',
      'mock_data',
      'test-data',
      'Role Visibility Problems'
    ];

    // ✅ NEW: Skip whitelisted files entirely
    if (isWhitelistedFile(fileName)) {
      return [];
    }

    // Check for mock data patterns
    MOCK_DATA_PATTERNS.forEach(({ pattern, severity, description }) => {
      lines.forEach((line, index) => {
        // Skip lines containing MockDetector keywords
        if (skipKeywords.some(kw => line.includes(kw))) return;
        
        // ✅ NEW: Skip whitelisted code patterns
        if (isWhitelistedCode(line)) return;
        
        if (pattern.test(line)) {
          const violation: MockViolation = {
            type: 'mock_data',
            severity,
            file: fileName,
            line: index + 1,
            code: line.trim(),
            description,
            fix: 'Replace with real data from your database or API',
            confidence: 0 // Will be calculated
          };
          
          // ✅ PHASE 3: Calculate confidence
          violation.confidence = calculateViolationConfidence(violation);
          
          // ✅ PHASE 4: Enhance with contextual auto-fix
          const enhanced = enhanceViolationWithFix(violation) as MockViolation;
          
          // Only add if confidence >= 50%
          if (enhanced.confidence && enhanced.confidence >= 50) {
            violations.push(enhanced);
          }
        }
      });
    });
    
    // Check for static data patterns (arrays/objects with hardcoded data)
    STATIC_DATA_PATTERNS.forEach(({ pattern, severity, description }) => {
      const matches = codeText.matchAll(pattern);
      for (const match of matches) {
        // Find line number
        const lineNumber = codeText.substring(0, match.index).split('\n').length;
        const line = lines[lineNumber - 1];
        
        // Skip if line contains skip keywords
        if (skipKeywords.some(kw => line?.includes(kw))) continue;
        
        // ✅ NEW: Skip whitelisted code patterns
        if (line && isWhitelistedCode(line)) continue;
        
        const violation: MockViolation = {
          type: 'mock_data',
          severity,
          file: fileName,
          line: lineNumber,
          code: line?.trim() || match[0].trim(),
          description,
          fix: 'Replace with data fetched from database or API. Use useState/useEffect to load dynamic data.',
          confidence: 0 // Will be calculated
        };
        
        // ✅ PHASE 3: Calculate confidence
        violation.confidence = calculateViolationConfidence(violation);
        
        // ✅ PHASE 4: Enhance with contextual auto-fix
        const enhanced = enhanceViolationWithFix(violation) as MockViolation;
        
        // Only add if confidence >= 50%
        if (enhanced.confidence && enhanced.confidence >= 50) {
          violations.push(enhanced);
        }
      }
    });

    // NEW: React-specific pattern scanning
    const reactViolations = scanReactCode(codeText, fileName);
    violations.push(...reactViolations);

    return violations;
  };

  const runScan = async (): Promise<ScanResult> => {
    setIsScanning(true);
    const startTime = performance.now();

    try {
      console.log('🔍 MockDetector: Starting aggressive deep scan...');
      
      // Phase 1: Basic pattern detection
      const domViolations = scanDOM();
      const bodyText = document.body.innerText;
      const currentRoute = window.location.pathname;
      const codeViolations = scanCode(bodyText, currentRoute);
      
      // Phase 2: Statistical claims detection
      console.log('🔍 Phase 3: Scanning for statistical claims...');
      const statisticalClaimViolations = scanStatisticalClaims();
      
      // Combine all violations
      const allViolations = [
        ...domViolations,
        ...codeViolations,
        ...statisticalClaimViolations
      ];

      // All violations are mock_data type now
      const mockDataViolations = allViolations;

      const result: ScanResult = {
        totalViolations: allViolations.length,
        mockDataViolations,
        scanDuration: performance.now() - startTime,
        scannedAt: new Date()
      };

      console.log(`✅ MockDetector: Scan complete. Found ${result.totalViolations} violations in ${result.scanDuration.toFixed(0)}ms`);
      
      // Calculate health score
      const health = calculateHealthScore(result, healthScore?.overall);
      setHealthScore(health);
      console.log(`📊 Health Score: ${health.overall}/100 (${health.grade})`);
      
      setScanResult(result);
      return result;
    } catch (error) {
      console.error('❌ MockDetector: Scan failed:', error);
      throw error;
    } finally {
      setIsScanning(false);
    }
  };

  // Helper: Create unique key for violation
  const getViolationKey = (v: MockViolation): string => {
    return `${v.type}|${v.description}|${v.code?.substring(0, 50) || ''}`;
  };

  // Helper: Filter duplicate violations
  const filterDuplicates = (violations: MockViolation[]): MockViolation[] => {
    const seen = new Map<string, { violation: MockViolation; routes: Set<string> }>();
    
    violations.forEach(v => {
      const key = getViolationKey(v);
      const existing = seen.get(key);
      
      if (!existing) {
        seen.set(key, {
          violation: v,
          routes: new Set(v.route ? [v.route] : [])
        });
      } else if (v.route && v.route !== existing.violation.route) {
        // Same violation on different routes - track all routes
        existing.routes.add(v.route);
      }
    });
    
    // Build final violations with route information
    return Array.from(seen.values()).map(({ violation, routes }) => {
      if (routes.size > 1) {
        const routeList = Array.from(routes).join(', ');
        return {
          ...violation,
          description: `${violation.description} (found on ${routes.size} routes: ${routeList})`
        };
      }
      return violation;
    });
  };

  const runMultiRouteScan = async (routes: string[]): Promise<ScanResult> => {
    setIsScanning(true);
    const startTime = performance.now();
    
    // Save current route to restore after scanning
    const originalRoute = window.location.pathname;
    
    // Smart timeout based on number of routes (safety net)
    const timeoutMs = routes.length < 10 ? 180000 :      // Small: 3 minutes
                      routes.length < 30 ? 300000 :      // Medium: 5 minutes  
                      600000;                            // Large: 10 minutes
    
    let timeoutId: number | null = null;
    let isTimedOut = false;
    
    try {
      console.log(`🔍 MockDetector: Starting multi-route scan for ${routes.length} routes...`);
      console.log(`📍 Original route: ${originalRoute} (will restore after scan)`);
      console.log(`⏱️ Timeout set to ${timeoutMs / 1000 / 60} minutes for safety`);
      
      // Set timeout to prevent infinite scanning (cost protection)
      timeoutId = window.setTimeout(() => {
        isTimedOut = true;
        console.warn(`⏱️ Multi-route scan timeout after ${timeoutMs / 1000 / 60} minutes`);
      }, timeoutMs);
      
      const allViolations: MockViolation[] = [];
      
      for (let i = 0; i < routes.length; i++) {
        // Check timeout
        if (isTimedOut) {
          console.warn(`⏱️ Multi-route scan timed out after ${i} routes`);
          setCurrentScanProgress(`⚠️ Timeout after ${i} routes - returning partial results`);
          break;
        }
        
        const route = routes[i];
        const progressPercent = Math.round((i / routes.length) * 100);
        const progress = `Scanning route ${i + 1}/${routes.length} (${progressPercent}%): ${route}`;
        console.log(`🔍 ${progress}`);
        setCurrentScanProgress(progress);
        
        // Navigate using React Router with replace to not mess up history
        navigate(route, { replace: true });
        
        // Wait longer for page to fully render (2 seconds)
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Run scan on this route
        const domViolations = scanDOM();
        const bodyText = document.body.innerText;
        const codeViolations = scanCode(bodyText, route); // Use actual route instead of 'current-page'
        const statisticalClaimViolations = scanStatisticalClaims();
        
        // Add route information to all violations from this scan
        const routeViolations = [
          ...domViolations,
          ...codeViolations,
          ...statisticalClaimViolations
        ].map(v => ({ ...v, route }));
        
        // Filter duplicates EARLY (per route)
        const uniqueRouteViolations = filterDuplicates(routeViolations);
        
        allViolations.push(...uniqueRouteViolations);
        
        console.log(`  ✓ Found ${uniqueRouteViolations.length} unique violations on ${route}`);
      }
      
      // Clear timeout
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
      
      // Final duplicate filtering across all routes
      const finalViolations = filterDuplicates(allViolations);
      
      // All violations are mock_data type now
      const mockDataViolations = finalViolations;
      
      const scanDuration = performance.now() - startTime;
      const result: ScanResult = {
        totalViolations: finalViolations.length,
        mockDataViolations,
        scanDuration,
        scannedAt: new Date()
      };
      
      if (isTimedOut) {
        console.log(`⚠️ PARTIAL multi-route scan: ${result.totalViolations} violations (timed out, ${scanDuration.toFixed(0)}ms)`);
        setCurrentScanProgress('⚠️ Timeout - Partial Results - Restoring route...');
      } else {
        console.log(`✅ MockDetector: Multi-route scan complete. Found ${result.totalViolations} unique violations across ${routes.length} routes`);
        setCurrentScanProgress('✅ Scan Complete - Restoring original route...');
      }
      
      // Restore original route
      console.log(`📍 Restoring original route: ${originalRoute}`);
      navigate(originalRoute, { replace: true });
      
      // Brief delay to ensure route restoration completes
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setCurrentScanProgress('✅ Scan Complete');
      setScanResult(result);
      return result;
    } catch (error) {
      console.error('❌ MockDetector: Multi-route scan failed:', error);
      setCurrentScanProgress('❌ Scan Failed');
      
      // Clear timeout on error
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
      
      // Always restore original route, even on error
      console.log(`📍 Restoring original route after error: ${originalRoute}`);
      navigate(originalRoute, { replace: true });
      
      throw error;
    } finally {
      setIsScanning(false);
    }
  };

  // NEW: Deep scan source files CLIENT-SIDE (works for ANY Lovable project!)
  // This scanner reads actual project files using Vite's import.meta.glob
  // Perfect for external developers - scans THEIR code, not ours!
  const scanSourceFiles = async (config?: SourceFileScanConfig): Promise<MockViolation[]> => {
    console.log('🔍 Starting CLIENT-SIDE source file scan (works in any Lovable project)...');
    
    try {
      console.log('📋 Scan config:', config || 'Using defaults');
      
      // ✅ Use scanProjectFiles from top-level hook (no hook call inside function!)
      const violations = await scanProjectFiles(config);

      console.log(`✅ Client-side source scan complete: ${violations.length} violations found`);
      
      return violations;
      
    } catch (error) {
      console.error('❌ Client-side source file scan failed:', error);
      return [];
    }
  };

  // NEW: Run deep scan (DOM + Source Files)
  const runDeepScan = async (): Promise<ScanResult> => {
    setIsScanning(true);
    setIsDeepScanning(true);
    const startTime = performance.now();
    
    // Timeout for deep scan (longer since it includes source files)
    const timeoutMs = 600000; // 10 minutes max
    let timeoutId: number | null = null;
    let isTimedOut = false;

    try {
      console.log('🔍 MockDetector: Starting DEEP SCAN (DOM + Source Files)...');
      console.log(`⏱️ Timeout set to ${timeoutMs / 1000 / 60} minutes for safety`);
      
      // Set timeout
      timeoutId = window.setTimeout(() => {
        isTimedOut = true;
        console.warn(`⏱️ Deep scan timeout after ${timeoutMs / 1000 / 60} minutes`);
      }, timeoutMs);
      
      // Phase 1: Regular DOM scan
      setCurrentScanProgress('Scanning DOM...');
      const domViolations = scanDOM();
      const bodyText = document.body.innerText;
      const currentRoute = window.location.pathname;
      const codeViolations = scanCode(bodyText, currentRoute); // Use actual route
      const statisticalClaimViolations = scanStatisticalClaims();
      
      // Phase 2: Deep source file scan
      setCurrentScanProgress(`Scanning source files (0 files)...`);
      const sourceFileViolations = await scanSourceFiles();
      
      // Combine all violations
      const allViolations = [
        ...domViolations,
        ...codeViolations,
        ...statisticalClaimViolations,
        ...sourceFileViolations
      ];

      // Clear timeout
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }

      const mockDataViolations = allViolations;

      const scanDuration = performance.now() - startTime;
      const result: ScanResult = {
        totalViolations: allViolations.length,
        mockDataViolations,
        scanDuration,
        scannedAt: new Date()
      };

      if (isTimedOut) {
        console.log(`⚠️ PARTIAL deep scan: ${result.totalViolations} violations (timed out, ${scanDuration.toFixed(0)}ms)`);
      } else {
        console.log(`✅ DEEP SCAN complete. Found ${result.totalViolations} violations in ${scanDuration.toFixed(0)}ms`);
      }
      console.log(`   - DOM scan: ${domViolations.length + codeViolations.length + statisticalClaimViolations.length}`);
      console.log(`   - Source files: ${sourceFileViolations.length}`);
      
      setCurrentScanProgress(isTimedOut ? '⚠️ Timeout - Partial Results' : '✅ Deep Scan Complete');
      setScanResult(result);
      return result;
    } catch (error) {
      console.error('❌ Deep scan failed:', error);
      
      // Clear timeout on error
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
      
      throw error;
    } finally {
      setIsScanning(false);
      setIsDeepScanning(false);
    }
  };

  // NEW: Smart Deep Scan - Auto-adapts based on project size
  const runSmartDeepScan = async (customConfig?: {
    includeEdgeFunctions?: boolean;
    includeConfigFiles?: boolean;
    customScanPath?: string;
  }): Promise<ScanResult> => {
    setIsScanning(true);
    setIsDeepScanning(true);
    const startTime = performance.now();
    const originalRoute = window.location.pathname;
    
    let timeoutId: number | null = null;
    let isTimedOut = false;

    try {
      console.log('🔍 MockDetector: Starting SMART DEEP SCAN...');
      
      // Phase 1: Detect project size
      setCurrentScanProgress('Analyzing project size...');
      const projectMetrics = await detectProjectSize();
      const projectSize: ProjectSize = projectMetrics.size;
      const allRoutes = await getAllRoutes();
      
      console.log(`📊 Project classified as: ${projectSize.toUpperCase()}`);
      console.log(`   - Routes: ${projectMetrics.routeCount}`);
      console.log(`   - Database: ${projectMetrics.databaseSize}`);
      console.log(`   - Estimated files: ${projectMetrics.estimatedSourceFiles}`);
      
      // Smart timeout based on project size
      const timeoutMs = projectSize === 'small' ? 180000 :      // Small: 3 minutes
                        projectSize === 'medium' ? 300000 :     // Medium: 5 minutes  
                        600000;                                 // Large: 10 minutes
      
      console.log(`⏱️ Timeout set to ${timeoutMs / 1000 / 60} minutes for ${projectSize} project`);
      
      // Set timeout
      timeoutId = window.setTimeout(() => {
        isTimedOut = true;
        console.warn(`⏱️ Smart deep scan timeout after ${timeoutMs / 1000 / 60} minutes`);
      }, timeoutMs);
      
      // Get scan config for this project size
      const sizeConfig = DEFAULT_SCAN_CONFIGS[projectSize];
      
      // Override with custom settings if provided
      const sourceFileConfig: SourceFileScanConfig = {
        ...sizeConfig.sourceFiles,
        ...(customConfig?.includeEdgeFunctions !== undefined && { includeEdgeFunctions: customConfig.includeEdgeFunctions }),
        ...(customConfig?.includeConfigFiles !== undefined && { includeConfigFiles: customConfig.includeConfigFiles }),
        ...(customConfig?.customScanPath && { rootPath: customConfig.customScanPath })
      };
      
      // Phase 2: Route-based scanning (adaptive)
      let routeViolations: MockViolation[] = [];
      const routesToScan = allRoutes.slice(0, sizeConfig.routes.maxRoutes);
      
      if (routesToScan.length > 0) {
        const isFullScan = routesToScan.length === allRoutes.length;
        console.log(`🔍 Scanning ${routesToScan.length} routes (${isFullScan ? 'all routes' : `first ${sizeConfig.routes.maxRoutes} of ${allRoutes.length}`})...`);
        
        for (let i = 0; i < routesToScan.length; i++) {
          // Check timeout
          if (isTimedOut) {
            console.warn(`⏱️ Smart scan timed out after ${i} routes`);
            setCurrentScanProgress(`⚠️ Timeout after ${i} routes - continuing to source files...`);
            break;
          }
          
          const route = routesToScan[i];
          const progressPercent = Math.round((i / routesToScan.length) * 100);
          const progress = `Scanning route ${i + 1}/${routesToScan.length} (${progressPercent}%): ${route}`;
          console.log(`🔍 ${progress}`);
          setCurrentScanProgress(progress);
          
          navigate(route, { replace: true });
          await new Promise(resolve => setTimeout(resolve, 1500));
          
          const domViolations = scanDOM();
          const bodyText = document.body.innerText;
          const codeViolations = scanCode(bodyText, route); // Use actual route
          const statisticalClaimViolations = scanStatisticalClaims();
          
          const violations = [
            ...domViolations,
            ...codeViolations,
            ...statisticalClaimViolations
          ].map(v => ({ ...v, route }));
          
          routeViolations.push(...filterDuplicates(violations));
        }
        
        // Restore original route
        navigate(originalRoute, { replace: true });
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      // Phase 3: Source file scan with adaptive config (skip if timed out)
      let sourceFileViolations: MockViolation[] = [];
      if (!isTimedOut) {
        setCurrentScanProgress(`Scanning source files (0 files)...`);
        sourceFileViolations = await scanSourceFiles(sourceFileConfig);
      } else {
        console.warn(`⏱️ Skipping source file scan due to timeout`);
      }
      
      // Combine all violations
      console.log(`📊 Before deduplication:`);
      console.log(`   - Route violations: ${routeViolations.length}`);
      console.log(`   - Source file violations: ${sourceFileViolations.length}`);
      console.log(`   - Total (with duplicates): ${routeViolations.length + sourceFileViolations.length}`);
      
      const allViolations = filterDuplicates([
        ...routeViolations,
        ...sourceFileViolations
      ]);
      
      const duplicatesRemoved = (routeViolations.length + sourceFileViolations.length) - allViolations.length;
      console.log(`   - Duplicates removed: ${duplicatesRemoved}`);
      console.log(`   - Final unique violations: ${allViolations.length}`);

      const mockDataViolations = allViolations;

      const result: ScanResult = {
        totalViolations: allViolations.length,
        mockDataViolations,
        scanDuration: performance.now() - startTime,
        scannedAt: new Date()
      };
      
      // Clear timeout
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }

      if (isTimedOut) {
        console.log(`⚠️ PARTIAL smart deep scan for ${projectSize} project (timed out)`);
      } else {
        console.log(`✅ SMART DEEP SCAN complete for ${projectSize} project`);
      }
      console.log(`   - Total violations: ${result.totalViolations}`);
      console.log(`   - Routes scanned: ${routesToScan.length}`);
      console.log(`   - Route violations: ${routeViolations.length}`);
      console.log(`   - Source file violations: ${sourceFileViolations.length}`);
      console.log(`   - Scan duration: ${result.scanDuration.toFixed(0)}ms`);
      
      // Calculate health score
      const health = calculateHealthScore(result, healthScore?.overall);
      setHealthScore(health);
      
      setCurrentScanProgress(isTimedOut ? '⚠️ Timeout - Partial Results' : '✅ Smart Deep Scan Complete');
      setScanResult(result);
      return result;
    } catch (error) {
      console.error('❌ Smart deep scan failed:', error);
      setCurrentScanProgress('❌ Scan Failed');
      
      // Clear timeout on error
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
      
      throw error;
    } finally {
      setIsScanning(false);
      setIsDeepScanning(false);
    }
  };

  return {
    isScanning,
    scanResult,
    setScanResult, // ✅ FIX: Export setter for clearing results
    runScan,
    runMultiRouteScan,
    runDeepScan, // Deep scan with source file analysis
    runSmartDeepScan, // NEW: Smart deep scan with auto-adaption
    isDeepScanning,
    currentScanProgress,
    scanCode,
    healthScore, // NEW: Project health score
    generateAutoFixes: () => scanResult ? generateAllAutoFixes(scanResult.mockDataViolations) : [],
    sourceFileProgress, // Progress percentage for source file scanning
    filesScanned, // Number of files scanned
    totalFiles, // Total files to scan
  };
};
